import { useState } from "react";
import {
  createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail,
} from "firebase/auth";
import {
  doc, getDoc, serverTimestamp, writeBatch, collection,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { S, C } from "../ui";
import { TIME_ZONES } from "../constants";
import lockupLight from "../assets/lockup-light.svg";

// Three top-level roles reach the app from this one screen (Phase 11
// added Staff — previously only Admin/Vendor existed here). Admin and
// Vendor share the same "Sign In" form underneath (a memberships/{uid}
// lookup post-auth resolves Facility Admin vs. Vendor Admin, see
// App.jsx) — only the top-level button and which *second* option sits
// next to Sign In differs. Staff is handled entirely by App.jsx
// (onStaffMode flips it into StaffAuthScreen at the top level, *before*
// this component even unmounts) rather than rendered here — see
// App.jsx's staffMode comment for why that placement matters.
export default function AuthScreen({ onStaffMode }) {
  const [mode, setMode] = useState("choose"); // choose | adminChoose | vendorChoose | signin | createFacility | joinVendor
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // shared fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  // createFacility-only
  const [facilityName, setFacilityName] = useState("");
  const [facilityAddress, setFacilityAddress] = useState("");
  // Defaults to the browser's own timezone when it's one of the options
  // offered below — genuinely useful here since whoever is signing up is
  // almost always physically at (or very near) the facility they're
  // registering. Falls back to Central otherwise (matches this app's
  // existing demo data). Drives checkMissedVisits' schedule-vs-now compare
  // in functions/index.js — see that file's FACILITY_TIME_ZONE comment.
  const [timeZone, setTimeZone] = useState(() => {
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TIME_ZONES.some(([v]) => v === detected) ? detected : "America/Chicago";
  });
  // joinVendor-only
  const [inviteCode, setInviteCode] = useState("");

  const [resetMsg, setResetMsg] = useState("");

  const reset = () => { setErr(""); setBusy(false); setResetMsg(""); };
  const back = (to = "choose") => { setMode(to); reset(); };

  const doSignIn = async () => {
    setBusy(true); setErr("");
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (e) {
      setErr("Incorrect email or password.");
    }
    setBusy(false);
  };

  // Same confirmation message either way, regardless of whether the email
  // matched an account — Firebase's own default template, no account
  // enumeration. Ported from CustodialView's Admin/Contractor login.
  const doForgotPassword = async () => {
    if (!email.trim()) { setErr("Enter your email above first, then click \"Forgot password?\""); return; }
    setBusy(true); setErr(""); setResetMsg("");
    try {
      await sendPasswordResetEmail(auth, email.trim());
    } catch (e) {
      // fall through — same generic message either way, see comment above
    }
    setResetMsg("If that email has an account, a reset link is on its way.");
    setBusy(false);
  };

  const doCreateFacility = async () => {
    if (!facilityName.trim() || !name.trim() || !email.trim() || password.length < 6) {
      setErr("Fill in every field — password needs at least 6 characters.");
      return;
    }
    setBusy(true); setErr("");
    try {
      const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
      const uid = cred.user.uid;
      const facilityRef = doc(collection(db, "facilities"));
      const batch = writeBatch(db);
      batch.set(facilityRef, {
        name: facilityName.trim(),
        address: facilityAddress.trim(),
        timeZone,
        ownerUid: uid,
        isPrimary: true,
        createdAt: serverTimestamp(),
      });
      batch.set(doc(db, "memberships", uid), {
        role: "facility_admin",
        facilityId: facilityRef.id,
        name: name.trim(),
        email: email.trim(),
        createdAt: serverTimestamp(),
      });
      await batch.commit();
    } catch (e) {
      setErr(e.code === "auth/email-already-in-use" ? "That email is already in use." : "Something went wrong — try again.");
    }
    setBusy(false);
  };

  const doJoinVendor = async () => {
    if (!inviteCode.trim() || !name.trim() || !email.trim() || password.length < 6) {
      setErr("Fill in every field — password needs at least 6 characters.");
      return;
    }
    setBusy(true); setErr("");
    try {
      const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
      const uid = cred.user.uid;
      const code = inviteCode.trim().toUpperCase();
      const inviteSnap = await getDoc(doc(db, "inviteCodes", code));
      if (!inviteSnap.exists() || inviteSnap.data().used) {
        setErr("That invite code is invalid or has already been used.");
        setBusy(false);
        return;
      }
      const { facilityId, vendorId } = inviteSnap.data();
      const batch = writeBatch(db);
      batch.set(doc(db, "memberships", uid), {
        role: "vendor_admin",
        facilityId, vendorId, inviteCode: code,
        name: name.trim(),
        email: email.trim(),
        createdAt: serverTimestamp(),
      });
      batch.update(doc(db, "inviteCodes", code), {
        used: true, usedByUid: uid, usedAt: serverTimestamp(),
      });
      await batch.commit();
    } catch (e) {
      setErr(e.code === "auth/email-already-in-use" ? "That email is already in use." : "Something went wrong — check the invite code and try again.");
    }
    setBusy(false);
  };

  if (mode === "choose") return (
    <div style={S.center}>
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
          <img src={lockupLight} alt="Edifice View" style={{ height: 36 }} />
        </div>
        <div style={{ ...S.sub, textAlign: "center" }}>Facility accountability, across every vendor.</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button style={S.btn(C.navy)} onClick={() => setMode("adminChoose")}>Admin</button>
          <button style={S.btn()} onClick={() => setMode("vendorChoose")}>Vendor</button>
          <button style={{ ...S.btn("transparent"), border: `1.5px solid ${C.accent}`, color: C.accent }} onClick={onStaffMode}>Staff</button>
        </div>
      </div>
    </div>
  );

  if (mode === "adminChoose") return (
    <div style={S.center}><div style={S.card}>
      <div style={S.title}>Admin</div>
      <div style={S.sub}>Sign in to your facility, or create a new one.</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <button style={S.btn(C.navy)} onClick={() => setMode("signin")}>Sign In</button>
        <button style={S.btn()} onClick={() => setMode("createFacility")}>Create a Facility Account</button>
      </div>
      <button style={{ ...S.ghostBtn, marginTop: 10 }} onClick={() => back()}>← Back</button>
    </div></div>
  );

  if (mode === "vendorChoose") return (
    <div style={S.center}><div style={S.card}>
      <div style={S.title}>Vendor</div>
      <div style={S.sub}>Sign in to your company's account, or join with an invite code.</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <button style={S.btn(C.navy)} onClick={() => setMode("signin")}>Sign In</button>
        <button style={{ ...S.btn("transparent"), border: `1.5px solid ${C.accent}`, color: C.accent }} onClick={() => setMode("joinVendor")}>Join as a Vendor</button>
      </div>
      <button style={{ ...S.ghostBtn, marginTop: 10 }} onClick={() => back()}>← Back</button>
    </div></div>
  );

  if (mode === "signin") return (
    <div style={S.center}><div style={S.card}>
      <div style={S.title}>Sign In</div>
      <div style={S.sub}>Facility Admins and Vendor Admins both sign in here.</div>
      {err && <div style={S.err}>{err}</div>}
      <input style={S.input} type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} autoFocus />
      <input style={S.input} type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && doSignIn()} />
      <button style={{ ...S.btn(C.navy), marginBottom: 10 }} disabled={busy} onClick={doSignIn}>{busy ? "Signing in…" : "Sign In"}</button>
      {resetMsg
        ? <div style={{ fontSize: 13, color: C.accent, textAlign: "center", marginBottom: 10 }}>{resetMsg}</div>
        : <button style={{ ...S.ghostBtn, marginBottom: 10 }} disabled={busy} onClick={doForgotPassword}>Forgot password?</button>}
      <button style={S.ghostBtn} onClick={() => back()}>← Back</button>
    </div></div>
  );

  if (mode === "createFacility") return (
    <div style={S.center}><div style={S.card}>
      <div style={S.title}>Create a Facility Account</div>
      <div style={S.sub}>You'll be this facility's admin — you can add vendors once it's set up.</div>
      {err && <div style={S.err}>{err}</div>}
      <input style={S.input} placeholder="Facility name (e.g. 500 Main St Office Tower)" value={facilityName} onChange={e => setFacilityName(e.target.value)} autoFocus />
      <input style={S.input} placeholder="Address" value={facilityAddress} onChange={e => setFacilityAddress(e.target.value)} />
      <select style={{ ...S.input, background: C.white }} value={timeZone} onChange={e => setTimeZone(e.target.value)}>
        {TIME_ZONES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
      </select>
      <input style={S.input} placeholder="Your name" value={name} onChange={e => setName(e.target.value)} />
      <input style={S.input} type="email" placeholder="Your email" value={email} onChange={e => setEmail(e.target.value)} />
      <input style={S.input} type="password" placeholder="Password (min 6 characters)" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && doCreateFacility()} />
      <button style={{ ...S.btn(), marginBottom: 10 }} disabled={busy} onClick={doCreateFacility}>{busy ? "Creating…" : "Create Facility"}</button>
      <button style={S.ghostBtn} onClick={() => back("adminChoose")}>← Back</button>
    </div></div>
  );

  if (mode === "joinVendor") return (
    <div style={S.center}><div style={S.card}>
      <div style={S.title}>Join as a Vendor</div>
      <div style={S.sub}>You'll need the invite code your facility contact sent you.</div>
      {err && <div style={S.err}>{err}</div>}
      <input style={{ ...S.input, textTransform: "uppercase" }} placeholder="Invite code" value={inviteCode} onChange={e => setInviteCode(e.target.value)} autoFocus />
      <input style={S.input} placeholder="Your name" value={name} onChange={e => setName(e.target.value)} />
      <input style={S.input} type="email" placeholder="Your email" value={email} onChange={e => setEmail(e.target.value)} />
      <input style={S.input} type="password" placeholder="Password (min 6 characters)" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && doJoinVendor()} />
      <button style={{ ...S.btn(C.green), marginBottom: 10 }} disabled={busy} onClick={doJoinVendor}>{busy ? "Joining…" : "Join"}</button>
      <button style={S.ghostBtn} onClick={() => back("vendorChoose")}>← Back</button>
    </div></div>
  );

  return null;
}
